import React, { FC, memo, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Box } from '@vio/atlas/helpers/Box';
import { PriceWatchBell } from '@vio/shared/src/priceWatch/components/PriceWatchBell';
import { PartialFiltersMatch } from 'components/HotelCard/PartialFiltersMatch/PartialFiltersMatch';
import { HotelCardContext } from 'components/HotelCardContext';
import { getOfferVariant } from 'components/Offer/Shared/utils';
import { OfferBox } from 'components/OfferBox/OfferBox';
import { PriceWatchCTAWrapper } from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper';
import { UnavailabilityMobile } from 'components/Unavailability/Mobile';
import { VacationRentalDetails } from 'components/VacationRentalDetails';
import { useHotelCardActions } from 'hooks/useHotelCardActions';
import { getParametrizedHotelContext, getParametrizedHotelInfoContext } from 'middleware/analytics/selectors';
import { getHotel, getIsSearchPending, getOfferAccessFlowSettings } from 'modules/sapiSearch/selectors';
import { getAppLockedDealConfig, getHotelTopOffer, getOfferEntity } from 'modules/sapiSearch/selectors';
import { getAnchorComplete, getAnchorHotelHasYourChoice } from 'modules/sapiSearch/selectors';
import { getListOfValues } from 'modules/search/selectors';
import { getIsOTAMode } from 'modules/search/selectors';
import { getPreferredRate, getSerializedLandingQuery } from 'modules/search/selectors';
import { toggle } from 'opticks';
import { getRankedAvailableFacilities } from 'utils/facilities';
import { SelectedElement } from 'utils/selectedElement';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { getClosestDataIdToEventTarget } from '@daedalus/core/src/_web/utils/browser';
import { AnalyticsContext } from '@daedalus/core/src/analytics/types/Events';
import { OfferVariant } from '@daedalus/core/src/offer/types/OfferVariant';
import { GalleryWithFetchPriority } from '../Gallery/GalleryWithFetchPriority';
import { HotelCardLayout } from '../HotelCardLayout/HotelCardLayout';
import { PropertySummary } from '../PropertySummary/PropertySummary';
const AvailabilityPaneWrapper = /*#__PURE__*/styled('div')({
  name: "AvailabilityPaneWrapper",
  class: "a19vmo95",
  propsAsIs: false
});
const SOURCE_COMPONENT = 'HotelCardMobile';

// Added as part of 366b54ed-price-difference-alert-meta-traffic
const useShowPriceDifferenceAlert = (isAnchor = false) => {
  const anchorComplete = useSelector(getAnchorComplete);
  const anchorHotelHasYourChoice = useSelector(getAnchorHotelHasYourChoice);
  const serializedLandingQuery = useSelector(getSerializedLandingQuery);
  const priceWatchId = serializedLandingQuery?.priceWatchId ?? undefined;
  const preferredRate = useSelector(getPreferredRate);
  return isAnchor && anchorComplete && !priceWatchId && Boolean(preferredRate) && !anchorHotelHasYourChoice && toggle('366b54ed-price-difference-alert-meta-traffic', false, true);
};

/**
 * Component responsible for composing components into `HotelCardLayout` slots
 */
const ComposedHotelCardComponent: FC<{
  isPriorityCard: boolean;
}> = ({
  isPriorityCard
}) => {
  const {
    hotelDetailsIsOpenOnLanding,
    hotelId,
    isAnchor,
    isDummyLoadingCard
  } = useContext(HotelCardContext);
  const hotel = useSelector(state => getHotel(state, hotelId));
  const listOfValues = useSelector(getListOfValues);
  const rankedFacilities = getRankedAvailableFacilities(hotel?.facilities, listOfValues);
  const isOTAMode = useSelector(getIsOTAMode);
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId));
  const topOffer = useSelector(state => getHotelTopOffer(state, hotelId));
  const isSearchPending = useSelector(getIsSearchPending);
  const {
    isAppLockedDeal
  } = useSelector(state => getAppLockedDealConfig(state, hotelId, offerEntity?.id));

  // Added as part of 7d6464d8-move-auth-to-ap
  const {
    isAppRequired
  } = useSelector(state => getOfferAccessFlowSettings(state, hotelId, offerEntity?.id));
  const {
    brandCashbackEnabled
  } = useBrand();
  const topOfferVariant = topOffer && getOfferVariant({
    isCheapest: false
  });
  const isTopOfferMemberDeal = topOfferVariant === OfferVariant.MemberDeal || toggle('7d6464d8-move-auth-to-ap', isAppLockedDeal, isAppRequired);
  const basicPropertyDetailsComponent = useMemo(() => hotelDetailsIsOpenOnLanding && isAnchor && <Box padding="s400">
          <VacationRentalDetails showVrDetails={hotel?.isVr} vrDetails={hotel?.vrDetails} rankedFacilities={rankedFacilities} />
        </Box>, [hotel, hotelDetailsIsOpenOnLanding, isAnchor, rankedFacilities]);
  const unavailabilityComponent = useMemo(() => <AvailabilityPaneWrapper className={cx(isTopOfferMemberDeal && `__specialBorder`)}>
        <UnavailabilityMobile hotelId={hotelId} sourceComponentPrefix={SOURCE_COMPONENT} />
      </AvailabilityPaneWrapper>, [hotelId, isTopOfferMemberDeal]);
  const filtersMatchSlot = useMemo(() => !isAnchor && <PartialFiltersMatch hasBasicPropertyDetails={hotelDetailsIsOpenOnLanding && isAnchor} />, [hotelDetailsIsOpenOnLanding, isAnchor]);

  // Added as part of feature-static-srp flag
  const isStaticSRPFeatureEnabled = toggle('feature-static-srp', false, true);
  const showPriceDifferenceAlert = useShowPriceDifferenceAlert(isAnchor);
  const {
    onShowHotelDetails,
    trackUserHotelCardClicked
  } = useHotelCardActions();
  const handleOnShowHotelDetails = useCallback(() => {
    const analyticsContext = {
      [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
      [AnalyticsContext.HotelInfoContext]: getParametrizedHotelInfoContext(hotelId)
    };
    onShowHotelDetails(hotelId, analyticsContext);
  }, [onShowHotelDetails, hotelId]);
  const handleHotelCardClick = useCallback((e: React.MouseEvent) => {
    trackUserHotelCardClicked({
      hotelId: hotelId,
      component: 'ListViewHotelCard',
      selectedElement: getClosestDataIdToEventTarget(e) || SelectedElement.BlankSpace
    });
    if (isDummyLoadingCard) {
      return false;
    }
    handleOnShowHotelDetails();
  }, [hotelId, handleOnShowHotelDetails, trackUserHotelCardClicked, isDummyLoadingCard]);
  return <HotelCardLayout isAnchor={isAnchor} isTopOfferMemberDeal={isTopOfferMemberDeal} showPriceDifferenceAlert={showPriceDifferenceAlert} gallerySlot={<GalleryWithFetchPriority isPriorityCard={isPriorityCard} />} propertySummarySlot={<PropertySummary hotelId={hotelId} />} watchButtonSlot={<PriceWatchCTAWrapper hotel={hotel} ctaComponent={PriceWatchBell} componentName="PriceWatchBell" searchIsCompleted={!isSearchPending} holdOutElement={null} />} basicPropertyDetailsSlot={basicPropertyDetailsComponent} filtersMatchSlot={filtersMatchSlot} onClick={handleHotelCardClick} offersSlot={!isStaticSRPFeatureEnabled && <OfferBox withHotelCard isLoading={isSearchPending} isOTAMode={isOTAMode} primaryOfferVariant="PrimaryMultiColumn" primaryOfferShouldShowTaxation={false} offerEntity={offerEntity} component={SOURCE_COMPONENT} isPrimaryOfferMemberDeal={isTopOfferMemberDeal} unavailabilitySlot={unavailabilityComponent} shouldRenderUnavailabilitySlot={isAnchor} showCashback={brandCashbackEnabled} cashbackVariant="short" />} />;
};
export const ComposedHotelCard = memo(ComposedHotelCardComponent);

require("./ComposedHotelCard.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ComposedHotelCard.tsx");